import React from 'react';

import { gameKey, gameThumbUrl, type GameSourceRecord } from './game-sources';
import { useGameRecords } from './useGameRecords';

export interface GameThumbState {
  /** The game's thumbnail URL, once known; undefined while it is looked up and for a game with no art. */
  src: string | undefined;
  /** The art is still being looked up, a failed read included while it is still to be read again. */
  loading: boolean;
}

const NO_IDS: readonly string[] = [];

/** After a failed read of a game's art, it is read again after each of these waits in turn (ms); then it is left. */
const RETRY_AFTER_MS: readonly number[] = [2000, 4000, 8000];

/**
 * A game's square thumbnail (`gameThumbUrl`), for a record of either shape. A full record's own art is used as is;
 * a SLIM catalogue record (no art bundle at all) has its full record read by id (`useGameRecords`, batched with every
 * other thumbnail drawn in the same render) and its art used once it lands. A record that carries an art bundle is a
 * full one already: whatever art it has is all there is, so it is never read again.
 *
 * A read that fails is not an answer that the game has no art: it is read again 2, 4 and then 8 seconds later (the
 * thumbs that failed together read again together), and only after the last one fails is the game drawn as one with no
 * art.
 *
 * For a render function that cannot call hooks, use `<GameThumb>`.
 */
export const useGameThumb = (game: GameSourceRecord | undefined): GameThumbState => {
  const own = game === undefined ? undefined : gameThumbUrl(game);
  const id = game === undefined ? '' : gameKey(game);
  const lookUp = game !== undefined && own === undefined && game.art_bundle === undefined && id !== '';
  // How many times this game's art has been read again after a failure: another game starts with none.
  const [retried, setRetried] = React.useState({ id, count: 0 });
  const attempt = retried.id === id ? retried.count : 0;
  const { byId, loading, failed } = useGameRecords(lookUp ? [id] : NO_IDS, lookUp, attempt);
  const retryIn = failed && attempt < RETRY_AFTER_MS.length ? RETRY_AFTER_MS[attempt] : undefined;
  React.useEffect(() => {
    if (retryIn === undefined) return undefined;
    const timer = setTimeout(() => setRetried({ id, count: attempt + 1 }), retryIn);
    return () => clearTimeout(timer);
  }, [id, attempt, retryIn]);
  if (!lookUp) return { src: own, loading: false };
  const full = byId.get(id);
  return { src: full === undefined ? undefined : gameThumbUrl(full), loading: loading || retryIn !== undefined };
};

export default useGameThumb;
